import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, AlertCircle, ChevronRight, BookOpen } from 'lucide-react';

/**
 * SNPSearchBar
 *
 * Searches across rsid, gene, conditions, and GWAS traits.
 *
 * Props:
 *   allSnps      SNP[]        full unfiltered result set
 *   onSnpClick   fn(snp)      called to open the detail modal
 */
const SNPSearchBar = ({ allSnps = [], onSnpClick }) => {
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [open, setOpen]         = useState(false);
  const inputRef                = useRef(null);
  const containerRef            = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Returns the first matching trait string for display, or null
  const getMatchingTrait = (snp, q) => {
    // Check GWAS traits
    const gwasTrait = snp.gwas?.traits?.find(t => t.toLowerCase().includes(q));
    if (gwasTrait) return { text: gwasTrait, source: 'gwas' };
    // Check conditions (pipe-separated)
    if (snp.conditions) {
      const matchedCondition = snp.conditions
        .split('|')
        .map(c => c.trim())
        .find(c => c.toLowerCase().includes(q) && c.toLowerCase() !== 'not specified' && c.toLowerCase() !== 'not provided');
      if (matchedCondition) return { text: matchedCondition, source: 'condition' };
    }
    return null;
  };

  const search = useCallback((raw) => {
    const q = raw.trim().toLowerCase();
    if (!q) {
      setResults([]);
      setNotFound(false);
      setOpen(false);
      return;
    }

    const normalised = /^\d+$/.test(q) ? 'rs' + q : q;

    // Exact rsID match — open directly
    const exactRsid = allSnps.find(s => s.rsid?.toLowerCase() === normalised);
    if (exactRsid) {
      setResults([]);
      setNotFound(false);
      setOpen(false);
      setQuery('');
      onSnpClick(exactRsid);
      return;
    }

    // Exact single-gene match — open directly
    const geneExact = allSnps.filter(s => s.gene?.toLowerCase() === q);
    if (geneExact.length === 1) {
      setResults([]);
      setNotFound(false);
      setOpen(false);
      setQuery('');
      onSnpClick(geneExact[0]);
      return;
    }

    // Collect all match types with a matchedTrait attached for display
    const rsidPrefix  = allSnps
      .filter(s => s.rsid?.toLowerCase().startsWith(normalised) && s.rsid?.toLowerCase() !== normalised)
      .map(s => ({ snp: s, matchedTrait: null, matchType: 'rsid' }));

    const geneExactMulti = geneExact
      .map(s => ({ snp: s, matchedTrait: null, matchType: 'gene' }));

    const genePrefix  = allSnps
      .filter(s => s.gene?.toLowerCase().startsWith(q) && s.gene?.toLowerCase() !== q)
      .map(s => ({ snp: s, matchedTrait: null, matchType: 'gene' }));

    const traitMatches = allSnps
      .filter(s => {
        // Skip if already matched by gene/rsid
        if (s.gene?.toLowerCase().startsWith(q)) return false;
        if (s.rsid?.toLowerCase().startsWith(normalised)) return false;
        return !!getMatchingTrait(s, q);
      })
      .map(s => ({ snp: s, matchedTrait: getMatchingTrait(s, q), matchType: 'trait' }));

    // Merge, deduplicate, prioritise: gene > rsid > trait
    const seen = new Set();
    const merged = [...geneExactMulti, ...rsidPrefix, ...genePrefix, ...traitMatches].filter(({ snp }) => {
      if (seen.has(snp.rsid)) return false;
      seen.add(snp.rsid);
      return true;
    });

    if (merged.length === 0) {
      setNotFound(true);
      setResults([]);
      setOpen(true);
    } else {
      setNotFound(false);
      setResults(merged.slice(0, 10));
      setOpen(true);
    }
  }, [allSnps, onSnpClick]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    search(val);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); setQuery(''); setNotFound(false); }
    if (e.key === 'Enter') search(query);
  };

  const handleSelect = (snp) => {
    setOpen(false);
    setQuery('');
    setNotFound(false);
    onSnpClick(snp);
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setNotFound(false);
    setOpen(false);
    inputRef.current?.focus();
  };

  const categoryIcons = {
    'pathogenic':        '🔴',
    'likely-pathogenic': '🟠',
    'risk':              '🟡',
    'protective':        '🟢',
    'drug-response':     '💊',
    'uncertain':         '🔵',
    'neutral':           '⚪',
    'likely-inactive':   '⚪',
  };

  const getQuickCategory = (snp) => {
    const sig = (snp.clinicalSignificance || '').toLowerCase();
    if (sig.includes('pathogenic') && !sig.includes('likely') && !sig.includes('benign')) return 'pathogenic';
    if (sig.includes('likely pathogenic')) return 'likely-pathogenic';
    if (sig.includes('risk factor')) return 'risk';
    if (sig.includes('drug response')) return 'drug-response';
    if (sig.includes('benign')) return 'protective';
    if (sig.includes('uncertain') || sig.includes('conflicting')) return 'uncertain';
    if (snp.gwas?.hasData) return 'neutral';
    return 'neutral';
  };

  // Group results by match type for sectioned display
  const geneResults  = results.filter(r => r.matchType === 'gene' || r.matchType === 'rsid');
  const traitResults = results.filter(r => r.matchType === 'trait');

  return (
    <div ref={containerRef} className="relative w-full">
      <div className={
        'flex items-center gap-2 px-3 py-2 bg-white border rounded-xl shadow-sm transition-all ' +
        (open ? 'border-indigo-400 ring-2 ring-indigo-100' : 'border-slate-200 hover:border-slate-300')
      }>
        <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => query && setOpen(true)}
          placeholder="Search by gene, rsID, or trait..."
          className="flex-1 text-sm bg-transparent outline-none text-slate-800 placeholder-slate-400"
        />
        {query && (
          <button onClick={handleClear} className="p-0.5 hover:bg-slate-100 rounded transition-colors">
            <X className="w-3.5 h-3.5 text-slate-400" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-xl border border-slate-200 shadow-xl z-50 overflow-hidden">
          {notFound ? (
            <div className="flex items-center gap-2.5 px-4 py-3.5 text-sm text-slate-500">
              <AlertCircle className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span>No variants found for <strong className="text-slate-700">"{query}"</strong></span>
            </div>
          ) : (
            <>
              {/* Gene / rsID results */}
              {geneResults.length > 0 && (
                <>
                  {traitResults.length > 0 && (
                    <div className="px-3 pt-2.5 pb-1">
                      <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Gene / rsID</span>
                    </div>
                  )}
                  {geneResults.map(({ snp }) => {
                    const cat = getQuickCategory(snp);
                    return (
                      <button
                        key={snp.rsid}
                        onClick={() => handleSelect(snp)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-indigo-50 transition-colors text-left group"
                      >
                        <span className="text-sm flex-shrink-0">{categoryIcons[cat] || '⚪'}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-slate-800 group-hover:text-indigo-700">{snp.rsid}</span>
                            <span className="text-xs text-slate-500 font-medium">{snp.gene}</span>
                            <span className="text-xs font-mono text-slate-400">{snp.genotype}</span>
                          </div>
                          {snp.conditions && (
                            <p className="text-xs text-slate-400 truncate">
                              {snp.conditions.split('|')[0].trim()}
                            </p>
                          )}
                          {!snp.conditions && snp.gwas?.traits?.[0] && (
                            <p className="text-xs text-violet-400 truncate">{snp.gwas.traits[0]}</p>
                          )}
                        </div>
                        <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-indigo-400 flex-shrink-0" />
                      </button>
                    );
                  })}
                </>
              )}

              {/* Trait results */}
              {traitResults.length > 0 && (
                <>
                  <div className={'px-3 pb-1 ' + (geneResults.length > 0 ? 'pt-2 border-t border-slate-100 mt-1' : 'pt-2.5')}>
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                      <BookOpen className="w-3 h-3" /> Traits & Conditions
                    </span>
                  </div>
                  {traitResults.map(({ snp, matchedTrait }) => {
                    const cat = getQuickCategory(snp);
                    return (
                      <button
                        key={snp.rsid}
                        onClick={() => handleSelect(snp)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-violet-50 transition-colors text-left group"
                      >
                        <span className="text-sm flex-shrink-0">{categoryIcons[cat] || '⚪'}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-slate-800 group-hover:text-violet-700">{snp.rsid}</span>
                            <span className="text-xs text-slate-500 font-medium">{snp.gene}</span>
                            <span className="text-xs font-mono text-slate-400">{snp.genotype}</span>
                          </div>
                          {matchedTrait && (
                            <p className={'text-xs truncate ' + (matchedTrait.source === 'gwas' ? 'text-violet-500' : 'text-slate-400')}>
                              {matchedTrait.source === 'gwas' && <BookOpen className="w-2.5 h-2.5 inline mr-1 opacity-70" />}
                              {matchedTrait.text}
                            </p>
                          )}
                        </div>
                        <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-violet-400 flex-shrink-0" />
                      </button>
                    );
                  })}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SNPSearchBar;